/**
 * useRefreshCooldown Hook
 * Manages refresh cooldown with countdown timer
 * Extracted from App.js to eliminate duplication
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Refresh cooldown hook with countdown
 *
 * @param {number} cooldownSeconds - Cooldown duration in seconds
 * @returns {Object} { isOnCooldown, remainingSeconds, startCooldown }
 */
const useRefreshCooldown = (cooldownSeconds = 60) => {
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const timerRef = useRef(null);

    const isOnCooldown = remainingSeconds > 0;

    const startCooldown = useCallback(() => {
        setRemainingSeconds(cooldownSeconds);

        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            setRemainingSeconds(prev => {
                if (prev <= 1) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [cooldownSeconds]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    return {
        isOnCooldown,
        remainingSeconds,
        startCooldown
    };
};

export default useRefreshCooldown;
